import { useEffect, useRef } from 'react';
import { useAccount } from '../../contexts/account/AccountContexts';
import Intercom, { boot, shutdown, update } from '@intercom/messenger-js-sdk';
import { TOKEN_KEY } from '../../services/auth/AuthService';
import { transformToIntercomUser } from '../../services/intercom/transformUser';
import { REACT_APP_INTERCOM_ENABLED, REACT_APP_INTERCOM_MESSENGER_ENABLED, REACT_APP_INTERCOM_APP_ID } from '../../utils/config';
import useIsMobile from '../../hooks/global/UseIsMobile';

/**
 * Boots the Intercom Messenger once per contact identity (`user_id`, i.e.
 * `${userId}.${accountId}`) and keeps it alive across account refetches.
 *
 * The session must NOT be restarted when `accountResponse` merely gets a new
 * object identity (silent refetches produce a fresh object on every call):
 * the SDK's `Intercom()` initialiser degrades to an `update` once the widget
 * is on the page, and after a `shutdown` only `boot` starts a new session.
 * Re-running shutdown+`Intercom()` therefore leaves the Messenger dead.
 *
 * `shutdown` therefore happens only on logout or identity switch. Same-identity
 * data changes go through `update`, guarded by a payload comparison: Intercom
 * throttles at 20 updates per 30 min per page and asks that we update only when
 * data actually changed, and our silent refetches fire far more often than that.
 *
 * No unmount cleanup on purpose: StrictMode remounts effects in dev, and a
 * cleanup-time `shutdown` would kill the session while the ref still claims
 * the contact is booted.
 */
export const IntercomChat = () => {
  const { accountResponse } = useAccount();
  const isMobile = useIsMobile();
  const bootedIdentityRef = useRef<string | null>(null);
  const lastSettingsRef = useRef<string | null>(null);

  useEffect(() => {
    if (!REACT_APP_INTERCOM_ENABLED) {
      return;
    }

    if (!accountResponse) {
      // A null account is NOT necessarily a logout: `refetchData()` nulls the context
      // on every hard refresh, and shutting down there would resurrect the very
      // restart cycle this component avoids. The token (removed before `logout()`
      // triggers the refetch) is what actually distinguishes the two.
      const isLoggedOut = localStorage.getItem(TOKEN_KEY) === null;
      if (isLoggedOut && bootedIdentityRef.current !== null) {
        // End the session so the next user on this browser starts clean.
        shutdown();
        bootedIdentityRef.current = null;
        lastSettingsRef.current = null;
      }
      return;
    }

    // Gość to klient agencji na CUDZYM koncie — wsparcie ChatBeata prowadzi właściciel, nie on.
    // Jeśli w tej karcie był wcześniej zalogowany ktoś inny, sesję trzeba domknąć, a nie tylko pominąć.
    if (accountResponse.role === 'GUEST') {
      if (bootedIdentityRef.current !== null) {
        shutdown();
        bootedIdentityRef.current = null;
        lastSettingsRef.current = null;
      }
      return;
    }

    const identity = accountResponse.intercomUserId;
    const settings = {
      ...transformToIntercomUser(accountResponse),
      app_id: REACT_APP_INTERCOM_APP_ID,
      // Telefon: bez pływającej ikonki (zasłaniała treść i dolny pasek) — czat otwiera „Pomoc” w pasku
      // (`MobileTabBar`). Zmiana szerokości okna idzie przez `update` jak każda inna zmiana ustawień.
      hide_default_launcher: !REACT_APP_INTERCOM_MESSENGER_ENABLED || isMobile,
    };
    const serialized = JSON.stringify(settings);

    if (bootedIdentityRef.current === identity) {
      // Same contact: push only genuine attribute changes. An `update` also pings
      // Intercom, so checklist steps resolved by fresh attributes show up at once.
      if (lastSettingsRef.current !== serialized) {
        update(settings);
        lastSettingsRef.current = serialized;
      }
      return;
    }

    if (typeof window.Intercom === 'function') {
      // Widget already on the page (identity switch, or re-login after logout).
      shutdown();
      boot(settings);
    } else {
      // First boot: injects the widget script and boots with these settings.
      Intercom(settings);
    }
    bootedIdentityRef.current = identity;
    lastSettingsRef.current = serialized;
  }, [accountResponse, isMobile]);

  return null;
};
